import { IS_NOT_PROD_ENV } from '@eigtech/ui-shared-environment-web'
import { FormDrawer, createForm } from '@eigtech/ui-shared-forms/shadcn'
import { Button } from '@eigtech/ui/components/button'
import { toast } from '@eigtech/ui/components/sonner'
import { Stack, StackSeparator } from '@eigtech/ui/components/stack'
import { TypographyH5 } from '@eigtech/ui/components/typopgraphy'
import { useDisclosure } from '@eigtech/ui/hooks/use-disclosure'
import { useKonamiCode } from '@eigtech/ui/hooks/use-konami-code'
import { OwnerUiTheme, ThemeColorSchema } from '@eigtech/white-label-types'
import { XIcon } from 'lucide-react'
import { useEffect } from 'react'
import { z } from 'zod'
import {
  OwnerUiThemeOverride,
  clearWhiteLabelThemeOverride,
  setWhiteLabelThemeOverride,
  useWhiteLabelServiceTheme,
  useWhiteLabelThemeOverride,
} from '../theme/applyWhiteLabelTheme'
import { toTinyColor } from '../theme/normalizeColor'

/**
 * A hidden theme editor: enter the konami code to overwrite the colors the white
 * label service serves for this site. The override is persisted to localStorage and
 * re-applied on every load, so it survives a refresh until it is reset.
 *
 * Kept out of prod.
 */
export function ThemeOverrideDrawer() {
  if (!IS_NOT_PROD_ENV) return null

  return <KonamiThemeOverrideDrawer />
}

function KonamiThemeOverrideDrawer() {
  const serviceTheme = useWhiteLabelServiceTheme()
  const themeOverride = useWhiteLabelThemeOverride()

  const { open, onOpen, setOpen } = useDisclosure()

  useKonamiCode(onOpen)

  const form = useForm({ defaultValues: toFormValues(serviceTheme, themeOverride) })
  const { reset } = form

  // The served theme lands after this mounts, and the override changes as the drawer
  // is used, so the fields are re-seeded from both every time the drawer opens.
  useEffect(() => {
    if (open) reset(toFormValues(serviceTheme, themeOverride))
  }, [open, reset, serviceTheme, themeOverride])

  function onSubmit(values: ThemeOverrideForm) {
    setWhiteLabelThemeOverride(toThemeOverride(values, serviceTheme))
    toast.success('Theme override applied.')
    setOpen(false)
  }

  function onResetToServiceTheme() {
    clearWhiteLabelThemeOverride()
    reset(toFormValues(serviceTheme, {}))
    toast.success('Theme override cleared.')
  }

  return (
    <FormDrawer
      description="Overwrite the theme the white label service serves for this site; a blank field falls back to the served value."
      form={form}
      open={open}
      submitButton={
        <>
          <Button type="button" variant="outline" onClick={onResetToServiceTheme}>
            Reset
          </Button>

          <form.SubmitButton size="md">Apply</form.SubmitButton>
        </>
      }
      title="Theme Override"
      onOpenChange={setOpen}
      onSubmit={onSubmit}
    >
      <Stack className="gap-6" separator={<StackSeparator />}>
        {COLOR_FIELD_GROUPS.map(({ fields, title }) => (
          <div key={title}>
            <TypographyH5>{title}</TypographyH5>

            <div className="grid grid-cols-2 gap-2">
              {fields.map(({ label, name }) => (
                <ColorField key={name} label={label} name={name} />
              ))}
            </div>
          </div>
        ))}
      </Stack>
    </FormDrawer>
  )
}

/** A text field for a color, with a native picker for the hex-pickable part of it. */
function ColorField({ label, name }: { label: string; name: ColorFieldName }) {
  const { field } = useController({ name })

  return (
    <InputField
      inputProps={{ placeholder: 'inherit' }}
      label={label}
      leftElement={
        <input
          aria-label={`${label} color picker`}
          className="size-5 shrink-0 cursor-pointer border-0 bg-transparent p-0"
          type="color"
          value={toTinyColor(field.value)?.toHexString() ?? '#000000'}
          onChange={(event) => field.onChange(event.target.value)}
        />
      }
      name={name}
      rightElement={<ClearFieldButton label={label} name={name} />}
    />
  )
}

/**
 * Blanks a single field, dropping its override so the field falls back to whatever
 * the white label service serves. Hidden while the field is already blank.
 */
function ClearFieldButton({ label, name }: { label: string; name: ColorFieldName }) {
  const { field } = useController({ name })

  if (!field.value) return null

  return (
    <Button
      aria-label={`Clear ${label}`}
      className="size-5"
      mode="icon"
      type="button"
      variant="ghost"
      onClick={() => field.onChange('')}
    >
      <XIcon />
    </Button>
  )
}

/** Every field of `OwnerUiThemeSchema` that holds a color. */
type ColorFieldName = Extract<keyof OwnerUiTheme, `${string}Color`>

const COLOR_FIELD_GROUPS = [
  {
    title: 'Brand',
    fields: [
      { name: 'primaryColor', label: 'Primary' },
      { name: 'primaryForegroundColor', label: 'Primary Foreground' },
      { name: 'secondaryColor', label: 'Secondary' },
      { name: 'secondaryForegroundColor', label: 'Secondary Foreground' },
      { name: 'accentColor', label: 'Accent' },
      { name: 'accentForegroundColor', label: 'Accent Foreground' },
      { name: 'mutedColor', label: 'Muted' },
      { name: 'mutedForegroundColor', label: 'Muted Foreground' },
    ],
  },
  {
    title: 'Controls',
    fields: [
      { name: 'borderColor', label: 'Border' },
      { name: 'inputColor', label: 'Input' },
      { name: 'ringColor', label: 'Ring' },
    ],
  },
  {
    title: 'Sidebar',
    fields: [
      { name: 'sidebarColor', label: 'Sidebar' },
      { name: 'sidebarForegroundColor', label: 'Sidebar Foreground' },
      { name: 'sidebarPrimaryColor', label: 'Sidebar Primary' },
      { name: 'sidebarPrimaryForegroundColor', label: 'Sidebar Primary Foreground' },
      { name: 'sidebarAccentColor', label: 'Sidebar Accent' },
      { name: 'sidebarAccentForegroundColor', label: 'Sidebar Accent Foreground' },
      { name: 'sidebarBorderColor', label: 'Sidebar Border' },
      { name: 'sidebarRingColor', label: 'Sidebar Ring' },
    ],
  },
] as const satisfies readonly {
  title: string
  fields: readonly { name: ColorFieldName; label: string }[]
}[]

type GroupedColorFieldName = (typeof COLOR_FIELD_GROUPS)[number]['fields'][number]['name']

const COLOR_FIELD_NAMES = COLOR_FIELD_GROUPS.flatMap(({ fields }) => fields.map(({ name }) => name))

/** A blank field means "keep whatever the white label service serves". */
const ColorFieldSchema = z.union([ThemeColorSchema, z.literal('')], {
  error: 'Must be a hex (#RRGGBB) or oklch() color',
})

// The `Record<ColorFieldName, …>` annotation fails to compile when a color is added
// to `OwnerUiThemeSchema` without a matching field in the groups above.
const colorFieldSchemas: Record<ColorFieldName, typeof ColorFieldSchema> = Object.fromEntries(
  COLOR_FIELD_NAMES.map((name) => [name, ColorFieldSchema])
) as Record<GroupedColorFieldName, typeof ColorFieldSchema>

const ThemeOverrideFormSchema = z.object(colorFieldSchemas)

type ThemeOverrideForm = z.infer<typeof ThemeOverrideFormSchema>

const { useForm, useController, InputField } = createForm(
  ThemeOverrideFormSchema,
  'whiteLabelThemeOverride'
)

/** Seed the form with the theme as it is currently painted. */
function toFormValues(
  serviceTheme: OwnerUiTheme | undefined,
  themeOverride: OwnerUiThemeOverride
): ThemeOverrideForm {
  const theme = { ...serviceTheme, ...themeOverride }

  return Object.fromEntries(
    COLOR_FIELD_NAMES.map((name) => [name, theme[name] ?? ''])
  ) as ThemeOverrideForm
}

/**
 * Keep only the fields that actually differ from the served theme, so a later change
 * to the white label config still shows through everywhere the developer did not
 * deliberately overwrite.
 */
function toThemeOverride(
  values: ThemeOverrideForm,
  serviceTheme: OwnerUiTheme | undefined
): OwnerUiThemeOverride {
  return Object.fromEntries(
    Object.entries(values).filter(
      ([name, value]) => !!value && value !== serviceTheme?.[name as keyof OwnerUiTheme]
    )
  )
}
